import type { RouteOptimizationResult } from '../entities/RouteOptimizationResult.js';
import { getEngineerProfitabilitySummary } from './getEngineerProfitability.js';
import { getRouteRevenueByEngineer } from './getRouteRevenueByEngineer.js';

export function getTotalProfitabilitySummary(selectedPlanning: RouteOptimizationResult) {
  const getEngineerByKey = (key: string) => {
    key = key.trim().toLowerCase();
    return (
      selectedPlanning.fieldEngineersAvailable?.find(engineer => engineer.id?.trim().toLowerCase() === key) ??
      selectedPlanning.partnerFieldEngineersAvailable?.find(engineer => engineer.id?.trim().toLowerCase() === key)
    );
  };

  const routeRevenueByEngineer = getRouteRevenueByEngineer(selectedPlanning);

  const summary = {
    ticketRevenue: 0,
    baseFeCost: 0,
    vehicleCost: 0,
    overtimeCost: 0,
    pudoCost: 0,
    adjustedFeCost: 0,
    profitDelta: 0,
    ticketCount: 0,
    routeMinutes: 0,
    availableTimeMinutes: 0,
    travelDistanceKm: 0,
    costFactorSum: 0,
    routeCount: 0,
    uniqueDepotCount: 0,
    ticketTypeBreakdown: {} as Record<string, number>,
  };

  for (const route of selectedPlanning.routes ?? []) {
    const routeEngineerSchedule = route.vehicleId ? getEngineerByKey(route.vehicleId) : undefined;
    const profitability = getEngineerProfitabilitySummary(
      {
        id: route.vehicleId,
        name: route.vehicleName,
        theoreticalStartTime: routeEngineerSchedule?.theoreticalStartTime,
        theoreticalEndTime: routeEngineerSchedule?.theoreticalEndTime,
        startTime: routeEngineerSchedule?.startTime,
        endTime: routeEngineerSchedule?.endTime,
      },
      routeRevenueByEngineer,
      selectedPlanning,
    );

    for (const stop of route.stops ?? []) {
      if (stop.type === 'ticket' && stop.ticketType) {
        summary.ticketTypeBreakdown[stop.ticketType] = (summary.ticketTypeBreakdown[stop.ticketType] ?? 0) + 1;
      }
    }

    summary.ticketRevenue += profitability.ticketRevenue;
    summary.baseFeCost += profitability.baseFeCost;
    summary.vehicleCost += profitability.vehicleCost;
    summary.overtimeCost += profitability.overtimeCost;
    summary.pudoCost += profitability.pudoCost;
    summary.adjustedFeCost += profitability.adjustedFeCost;
    summary.profitDelta += profitability.profitDelta;
    summary.ticketCount += profitability.ticketCount;
    summary.routeMinutes += profitability.routeMinutes;
    summary.availableTimeMinutes += profitability.scheduleMinutes ?? 0;
    summary.travelDistanceKm += profitability.travelDistanceKm;
    summary.costFactorSum += profitability.feCostFactor;
    summary.uniqueDepotCount += profitability.uniqueDepotCount;
    summary.routeCount += 1;
  }

  return {
    ticketCount: summary.ticketCount,
    ticketRevenue: summary.ticketRevenue,
    area: 'Total',
    travelDistanceKm: summary.travelDistanceKm,
    scheduleMinutes: summary.routeCount > 0 ? summary.availableTimeMinutes : null,
    routeMinutes: summary.routeMinutes,
    isPartnerFieldEngineer: false,
    baseFeCost: summary.baseFeCost,
    feCostFactor: summary.routeCount > 0 ? summary.costFactorSum / summary.routeCount : 0,
    vehicleCost: summary.vehicleCost,
    overtimeCost: summary.overtimeCost,
    pudoCost: summary.pudoCost,
    uniqueDepotCount: summary.uniqueDepotCount,
    adjustedFeCost: summary.adjustedFeCost,
    profitDelta: summary.profitDelta,
    ticketTypeBreakdown: summary.ticketTypeBreakdown,
  };
}

export function getRouteOptimizationProfitability(selectedPlanning: RouteOptimizationResult) {
  return getTotalProfitabilitySummary(selectedPlanning).profitDelta;
}
